import { ERowTypes } from "api/enums";
import {
  ParentRowIsNotStageException,
  ParentRowNotFoundException,
} from "api/exceptions/ContractRowException";
import { GreaterThanException } from "api/exceptions/InvalidValueException";
import { IRowToCancel } from "api/interfaces";
import ContractModel from "api/models/ContractModel";
import ContractRowModel from "api/models/ContractRowModel";
import ContractRowMovimentService from "api/services/ContractRowMovimentService";
import ContractRowService from "api/services/ContractRowService";
import ContractService from "api/services/ContractService";
import TransactionService from "api/services/TransactionService";
import { Transaction } from "firebase/firestore";
import { lang } from "i18nLang";
import { makeAutoObservable } from "mobx";
import { ContractRowStore, ContractStore, UserStore } from "store";
import { decimal } from "utils/DecimalPrecision";

class Store {
  constructor() {
    makeAutoObservable(this);
  }

  async getRowsMovements(row) {
    if (!row) return;
    return ContractRowMovimentService.getRowMoviment(row);
  }

  /** Altera a quantidade de uma linha do contrato e atualiza os totais das
   * etapas acimas e do contrato. No final salva a movimentação para histórico. */
  async updateRowsQuantity(rowIndex: number, updatedQuantity: number) {
    const row = ContractRowStore.getRowByIndex(rowIndex);
    if (!row) return;

    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const authenticatedUser = UserStore.currentUser;
    if (!authenticatedUser) return;

    await TransactionService.run(async (transaction) => {
      const oldTotal = row.total;
      await ContractRowService.updateResourceRowsQuantity(
        row,
        updatedQuantity,
        transaction
      );

      const parents = ContractRowStore.findParentsByCode(row.structured);
      const diff = decimal(row.total).subtract(oldTotal).toNumber();
      const newContractTotal =
        await this.recalculateAllParentsAndContractsTotals(
          contract,
          parents,
          diff,
          transaction
        );

      await ContractRowMovimentService.createUpdatedQuantityMoviment(
        row,
        updatedQuantity,
        newContractTotal,
        authenticatedUser,
        transaction
      );
    });
  }

  /** Cria uma linha aditivada no contrato e atualiza os totais das etapas acimas e
   * do contrato. No final salva a movimentação para histórico. */
  async createAdditiveRow(data: {
    structured: string;
    description: string;
    quantity: number;
    unit: string;
    unitCost: number;
  }): Promise<ContractRowModel | undefined> {
    const validation = this.verifyNewCode(data.structured);
    if (validation && validation.valid === false) return;

    this.verifyRowParent(data.structured);

    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const authenticatedUser = UserStore.currentUser;
    if (!authenticatedUser) return;

    let newRow: ContractRowModel | undefined = undefined;
    await TransactionService.run(async (transaction) => {
      const parents = ContractRowStore.findParentsByCode(data.structured);
      const parent = parents.length ? parents[parents.length - 1] : undefined;

      const countSiblings = ContractRowStore.countSiblingsByParentUid(
        parent?.uid || "-1"
      );

      newRow = await ContractRowService.createResourceRow(
        contract.uid,
        {
          ...data,
          structured: data.structured,
          parentStage: parent?.uid,
          parentStructured: parent?.structured,
          rowIndex: countSiblings,
        },
        transaction
      );

      const newContractTotal =
        await this.recalculateAllParentsAndContractsTotals(
          contract,
          parents,
          newRow.total,
          transaction
        );

      await ContractRowMovimentService.createAdditiveRowMoviment(
        newRow,
        newContractTotal,
        authenticatedUser,
        transaction
      );
    });
    return newRow;
  }

  /** Soma a quantidade aditivada a linha do contrato e atualiza os totais das etapas acimas e
   * do contrato. No final salva a movimentação para histórico. */
  async createAdditiveQuantity(
    structured: string,
    addQuantity: number
  ): Promise<ContractRowModel | undefined> {
    const row = ContractRowStore.getRowByCode(structured);
    if (!row || row.isStage) return;

    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const authenticatedUser = UserStore.currentUser;
    if (!authenticatedUser) return;

    await TransactionService.run(async (transaction) => {
      const oldTotal = row.total;
      const newQuantity = decimal(row.quantity).sum(addQuantity).toNumber();
      await ContractRowService.updateResourceRowsQuantityAndCancelIfNecessary(
        row,
        newQuantity,
        transaction
      );

      const parents = ContractRowStore.findParentsByCode(structured);
      const diff = decimal(row.total).subtract(oldTotal).toNumber();
      const newContractTotal =
        await this.recalculateAllParentsAndContractsTotals(
          contract,
          parents,
          diff,
          transaction
        );

      await ContractRowMovimentService.createAdditiveQuantityMoviment(
        row,
        addQuantity,
        newContractTotal,
        authenticatedUser,
        transaction
      );
    });
    return row;
  }

  public async createCancellationMass(
    structureds: string[]
  ): Promise<ContractRowModel[]> {
    const serviceRows = this.getServicesRowsToCancel(structureds);
    const datas = serviceRows.map((row) => ({
      row,
      quantity: row.getBalanceQuantity(),
    }));
    await this.cancellationRowsQuantity(datas);
    return serviceRows;
  }

  private getServicesRowsToCancel(structureds: string[]): ContractRowModel[] {
    const rows: ContractRowModel[] = [];
    for (const structured of structureds) {
      const row = ContractRowStore.getRowByCode(structured);
      if (!row) continue;

      if (row.isStage) {
        const children = ContractRowStore.getChildrenByCode(row.structured);
        const services = children.filter((row) => !row.isStage);
        this.insertServicesToRows(rows, services);
      } else {
        this.insertServicesToRows(rows, [row]);
      }
    }
    return rows;
  }

  private insertServicesToRows(
    rows: ContractRowModel[],
    rowsToInsert: ContractRowModel[]
  ) {
    for (const row of rowsToInsert) {
      if (!rows.find((r) => r.uid === row.uid)) {
        rows.push(row);
      }
    }
  }

  /** Distrata toda a linha do contrato, ou seja, zera sua quantidade e total. */
  public async createCancellationRow(
    structured: string
  ): Promise<ContractRowModel | undefined> {
    const row = ContractRowStore.getRowByCode(structured);
    if (!row || row.isStage) return;

    const data = [{ row, quantity: row.getBalanceQuantity() }];
    await this.cancellationRowsQuantity(data);
    return row;
  }

  /** Distrata uma quantidade da linha do contrato. */
  public async createCancellationQuantity(
    structured: string,
    subQuantity: number
  ): Promise<ContractRowModel | undefined> {
    const row = ContractRowStore.getRowByCode(structured);
    if (!row || row.isStage) return;

    const data = [{ row, quantity: subQuantity }];
    await this.cancellationRowsQuantity(data);
    return row;
  }

  /** Subtrai a quantidade distratada da linha do contrato e atualiza os totais das etapas acimas e
   * do contrato. No final salva a movimentação para histórico. */
  private async cancellationRowsQuantity(
    datas: {
      row: ContractRowModel;
      quantity: number;
    }[]
  ) {
    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const authenticatedUser = UserStore.currentUser;
    if (!authenticatedUser) return;

    const hasQuantityZero = datas.some((dt) => dt.quantity <= 0);
    if (hasQuantityZero) {
      throw new GreaterThanException(0, 0, "quantity");
    }

    await TransactionService.run(async (transaction) => {
      await this.updateRowsToCancelQuantity(datas, transaction);
      const newContractTotal = await this.recalculateParentsOfCanceledRows(
        contract,
        datas,
        transaction
      );
      const promises = datas.map((data) =>
        ContractRowMovimentService.createCancellationMoviment(
          data.row,
          data.quantity,
          newContractTotal,
          authenticatedUser,
          transaction
        )
      );
      await Promise.all(promises);
    });
  }

  private async updateRowsToCancelQuantity(
    datas: IRowToCancel[],
    transaction: Transaction
  ) {
    const promises: Promise<any>[] = [];
    for (const data of datas) {
      data.oldTotal = data.row.total;
      const newQuantity = decimal(data.row.quantity)
        .subtract(data.quantity)
        .toNumber();
      promises.push(
        ContractRowService.updateResourceRowsQuantityAndCancelIfNecessary(
          data.row,
          newQuantity,
          transaction
        )
      );
    }
    await Promise.all(promises);
  }

  private async recalculateParentsOfCanceledRows(
    contract: ContractModel,
    datas: IRowToCancel[],
    transaction: Transaction
  ) {
    let newContractTotal = 0;
    const allParentsUpdated: ContractRowModel[] = [];

    // Soma as diferenças nas linhas acimas e atualiza o total
    for (const data of datas) {
      const parents = ContractRowStore.findParentsByCode(data.row.structured);
      const diff = decimal(data.row.total)
        .subtract(data.oldTotal || 0)
        .toNumber();

      for (const parent of parents) {
        const rowTotal = decimal(parent.total).sum(diff).toNumber();
        parent.total = rowTotal;
      }

      newContractTotal = decimal(contract.total).sum(diff).toNumber();
      contract.total = newContractTotal;

      this.addItemsToUpdatedParentsList(allParentsUpdated, parents);
    }

    // Salva os novos valores no banco
    const promises = allParentsUpdated.map((parent) =>
      ContractRowService.updateRowsTotal(parent, parent.total, transaction)
    );
    await Promise.all(promises);
    await ContractService.updateTotal(contract, newContractTotal, transaction);
    return newContractTotal;
  }

  private addItemsToUpdatedParentsList(
    list: ContractRowModel[],
    parents: ContractRowModel[]
  ) {
    for (const parent of parents) {
      if (!list.find((item) => item.uid === parent.uid)) {
        list.push(parent);
      }
    }
  }

  /** Percorre a lista de etapas pais adicionando a diferença a seus totais, no final
   * soma a diferença no valor total do contrato e retorna o novo total do contrato.
   */
  private async recalculateAllParentsAndContractsTotals(
    contract: ContractModel,
    parents: ContractRowModel[],
    diffTotal: number,
    transaction: any
  ): Promise<number> {
    for (const parent of parents) {
      const rowTotal = decimal(parent.total).sum(diffTotal).toNumber();
      await ContractRowService.updateRowsTotal(parent, rowTotal, transaction);
      parent.total = rowTotal;
    }

    const newContractTotal = decimal(contract.total).sum(diffTotal).toNumber();
    await ContractService.updateTotal(contract, newContractTotal, transaction);

    return newContractTotal;
  }

  verifyNewCode(code: string) {
    const row = ContractRowStore.getRowByCode(code);
    if (!row) return;

    return {
      valid: false,
      text: lang("addsAndCancels.messages.error.existedCode"),
    };
  }

  verifyExistedCode(code: string, acceptStages: boolean = false) {
    const row = ContractRowStore.getRowByCode(code);
    if (row) {
      if (!row.isStage || acceptStages) return;

      return {
        valid: false,
        text: lang("addsAndCancels.messages.error.invalidCode"),
      };
    }

    return {
      valid: false,
      text: lang("addsAndCancels.messages.error.inexistedCode"),
    };
  }

  verifyRowMeasured(code: string, quantity: number) {
    const row = ContractRowStore.getRowByCode(code);
    if (row) {
      if (!row.isStage) {
        const serviceQuantity = decimal(row.quantity)
          .subtract(quantity)
          .toNumber();

        // Novo valor não bate com a medição acumulada.
        if (
          decimal(serviceQuantity).isLessThan(row.getMeasured().quantity || 0)
        )
          return {
            valid: false,
            text: lang("addsAndCancels.messages.error.invalidQuantity"),
          };
      }
    }

    return;
  }

  verifyRowParent(code: string) {
    if (!code.includes(".")) return true;

    const parentCode = code.split(".").slice(0, -1).join(".");
    const parentRow = ContractRowStore.getRowByCode(parentCode);

    if (!parentRow) {
      throw new ParentRowNotFoundException(parentCode);
    }

    if (parentRow.rowType === ERowTypes.RESOURCE) {
      throw new ParentRowIsNotStageException(parentCode);
    }

    return true;
  }
}

const ContractRowMovementStore = new Store();
export default ContractRowMovementStore;
